import { h, FunctionComponent } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { Trans, useTranslation } from 'react-i18next';
import { batch, useDispatch, useSelector } from 'react-redux';
import * as reacceptFormActions from '~src/domains/reaccept/state/reacceptFormReducer';
import { updateFormMeta } from '~src/utils/forms';
import { formatCustomerNames } from '~src/utils/helpers';
import { trackClick, trackFormBlur, trackPageMount } from '~src/utils/tracking';
import AutoPayCheckbox from '../accept/components/AutoPayCheckbox';
import AutoPayModal from '../accept/components/AutoPayModal';
import { Page } from '../common/enums/api';
import { AcceptFormNames, PaymentOption } from '../common/enums/common';
import {
  customerInfoSelector,
  customerNamesSelector,
  isOfferExpiredSelector,
  isOnboardSpendSelector,
  loanOfferSelectionSelector,
  loanOffersSelector,
  localeSelector,
  pageSelector,
  paymentMethodSelector,
  paymentOptionsSelector,
  sessionAutoPaySelector,
} from '../common/state/commonSelectors';
import { reacceptFormSelector } from './state/reacceptSelectors';
import * as styles from '~src/styles/domains/accept/AcceptPage.module.scss';
import CurrencyText from '../common/components/CurrencyText';
import OfferSelectors from '../accept/components/OfferSelectors';
import SimpleButton from '../common/components/Button';
import { autoPayEnabledSelector } from '../accept/state/acceptSelectors';
import { xhrNext } from '~src/utils/xhr';
import { resetLazyDocs, setDocument } from '../common/state/commonReducer';
import { setDocOrder } from '../loanConsent/state/loanConsentReducer';

const ReacceptPage: FunctionComponent = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [autoPayModalVisible, setAutoPayModalVisible] =
    useState<boolean>(false);

  const page = useSelector(pageSelector);
  const locale = useSelector(localeSelector);
  const sessionAutoPayEnabled = useSelector(sessionAutoPaySelector);
  const isOfferExpired = useSelector(isOfferExpiredSelector);
  const reacceptForm = useSelector(reacceptFormSelector);
  const customerNames = useSelector(customerNamesSelector);
  const loanOffers = useSelector(loanOffersSelector);
  const paymentOptions = useSelector(paymentOptionsSelector);
  const loanOfferSelection = useSelector(loanOfferSelectionSelector);
  const isAutoPayEnabled = useSelector(autoPayEnabledSelector);
  const selectedPaymentMethod = useSelector(paymentMethodSelector);
  const isOnboardSpend = useSelector(isOnboardSpendSelector);
  const customerInfo = useSelector(customerInfoSelector);

  const inputs = t('reaccept:inputs', {
    returnObjects: true,
  });
  const firstName = customerInfo.first_name
    ? customerInfo.first_name
    : formatCustomerNames(customerNames).firstName;
  const offer = loanOffers.find(offer => offer.offer_id === loanOfferSelection);
  const isCreditEnabled = paymentOptions.find(
    (option: PaymentOption) => option === PaymentOption.CREDIT,
  );
  const isAchEnabled = paymentOptions.find(
    (option: PaymentOption) => option === PaymentOption.BANK_ACCOUNT,
  );

  useEffect(() => {
    batch(() => {
      dispatch(trackPageMount(page as Page));
      dispatch(resetLazyDocs());
      dispatch(
        setDocument({
          id: null,
          content: null,
          url: null,
          show: false,
          loading: false,
        }),
      );
      dispatch(setDocOrder([]));
    });
  }, []);

  useEffect(() => {
    if (typeof inputs === 'string') {
      return;
    }
    dispatch(updateFormMeta(inputs, reacceptFormActions));
  }, [locale]);

  // Update autopay value with session value
  useEffect(() => {
    dispatch(
      reacceptFormActions.updateInputValue({
        name: AcceptFormNames.AUTOPAY,
        value: sessionAutoPayEnabled,
      }),
    );
  }, [sessionAutoPayEnabled]);

  const onAutoPayChange = (value: boolean) => {
    if (value === false) {
      setAutoPayModalVisible(true);
    }

    batch(() => {
      dispatch(
        reacceptFormActions.updateInputValue({
          name: AcceptFormNames.AUTOPAY,
          value,
        }),
      );
      dispatch(trackClick(AcceptFormNames.AUTOPAY, { value }));
    });
  };

  const onAutoPayBlur = () => {
    dispatch(trackFormBlur(AcceptFormNames.AUTOPAY));
  };

  const submitForm = () => {
    dispatch(xhrNext());
  };

  return offer ? (
    <div className={styles.accept}>
      <div className={styles['accept-header-wrapper']}>
        <div className='header-txt'>
          <Trans
            t={t}
            i18nKey={
              isOfferExpired
                ? firstName
                  ? 'accept:header-expired.name'
                  : 'accept:header-expire.no-name'
                : 'accept:header'
            }
            components={{
              payment: (
                <CurrencyText value={offer.order_amount} locale={locale} />
              ),
            }}
            values={{ name: firstName }}
          />
        </div>
        <div className={styles['accept-subheader']}>
          {offer.pay_today > 0
            ? t('accept:subheader')
            : t(
                `accept:subheader-zero-down${
                  isOnboardSpend ? '-onboard-spend' : ''
                }`,
              )}
        </div>
      </div>
      <OfferSelectors offers={loanOffers} />
      <div className={styles['accept-body']}>
        <AutoPayCheckbox
          id={AcceptFormNames.AUTOPAY}
          checked={
            (reacceptForm?.[AcceptFormNames.AUTOPAY]?.value as boolean) ?? true
          }
          onChangePost={onAutoPayChange}
          onBlurHandler={onAutoPayBlur}
        />
        <SimpleButton
          label={t('accept:submit')}
          trackingLabel={Page.ACCEPT}
          classLabel={
            isOfferExpired
              ? `btn-primary ${styles['accept-form-submit']} disabled`
              : `btn-primary ${styles['accept-form-submit']}`
          }
          trackClickPayload={
            isCreditEnabled || isAutoPayEnabled || isAchEnabled
              ? {
                  'payment-method': selectedPaymentMethod,
                  label: t('accept:submit'),
                  autopay: isAutoPayEnabled,
                }
              : {}
          }
          onClickPost={submitForm}
          isDisabled={isOfferExpired}
        />
        <AutoPayModal
          isAutoPayModalVisible={autoPayModalVisible}
          setAutoPayModalVisible={setAutoPayModalVisible}
          updateInputValue={reacceptFormActions.updateInputValue}
        />
      </div>
    </div>
  ) : null;
};

export default ReacceptPage;
